import * as React from 'react';
import { createPortal } from 'react-dom';
import { TourCutout } from './TourCutout';
import { TourStepCard } from './TourStepCard';
import { useAnchorRect } from './useAnchorRect';
import { dismissTour, isDismissedLocally } from './tourApi';
import { TOURS } from './tourRegistry';
import type { TourDefinition, TourHostProps } from './types';

const { useState, useEffect, useMemo, useRef, useCallback } = React;

const DEFAULT_START_DELAY = 1200;
const DEFAULT_ANCHOR_TIMEOUT = 2000;

/**
 * Picks at most one eligible tour and runs it.
 *
 * Eligibility is decided from props seeded by the server (TourHelper), never a
 * fetch — a fetch-then-show flow guarantees either a flash of the tour for
 * already-dismissed users or a visible delay for everyone.
 */
export const TourHost: React.FC<TourHostProps> = ({
  dismissals, isPractice, isProfessional, didiEnabled,
}) => {
  const ctx = useMemo(
    () => ({ isPractice, isProfessional, didiEnabled }),
    [isPractice, isProfessional, didiEnabled],
  );

  const tour: TourDefinition | null = useMemo(() => {
    const dismissed = new Set((dismissals || []).map((d) => `${d.key}:${d.version}`));
    return (
      TOURS.find(
        (t) =>
          t.audience(ctx) &&
          !dismissed.has(`${t.key}:${t.version}`) &&
          !isDismissedLocally(t.key, t.version),
      ) || null
    );
  }, [dismissals, ctx]);

  const [stepIndex, setStepIndex] = useState<number | null>(null);
  const startedRef = useRef(false);
  const exitedRef = useRef(false);

  // Start once, after a beat, so the page has settled first. The ref makes
  // React 18 StrictMode's double-effect a no-op instead of two timers.
  useEffect(() => {
    if (!tour || startedRef.current) return undefined;
    startedRef.current = true;
    const timer = window.setTimeout(
      () => setStepIndex(0),
      tour.startDelayMs ?? DEFAULT_START_DELAY,
    );
    return () => window.clearTimeout(timer);
  }, [tour]);

  const step = tour && stepIndex !== null ? tour.steps[stepIndex] : null;

  // Put the app into the state this step needs before we look for its anchor.
  useEffect(() => {
    step?.beforeShow?.();
  }, [step]);

  const { rect } = useAnchorRect(
    step ? step.anchor : null,
    step?.anchorTimeoutMs ?? DEFAULT_ANCHOR_TIMEOUT,
    stepIndex ?? 0,
  );

  const finish = useCallback(
    (type: 'completed' | 'skipped') => {
      if (!tour || exitedRef.current) return;
      exitedRef.current = true;
      // Hide first, persist after — a failed POST must never trap the user.
      setStepIndex(null);
      tour.onExit?.();
      dismissTour(tour.key, tour.version, type);
    },
    [tour],
  );

  const goNext = useCallback(() => {
    if (!tour || stepIndex === null) return;
    if (stepIndex >= tour.steps.length - 1) finish('completed');
    else setStepIndex(stepIndex + 1);
  }, [tour, stepIndex, finish]);

  const goBack = useCallback(() => {
    setStepIndex((i) => (i === null || i <= 0 ? i : i - 1));
  }, []);

  // Stable identity: TourStepCard re-registers its document keydown listener
  // whenever this changes, so an inline arrow would churn it on every render.
  const skip = useCallback(() => finish('skipped'), [finish]);

  // An anchor that never appears means the feature isn't on this page. Skip the
  // step rather than stranding the user on an empty highlight.
  useEffect(() => {
    if (!step || rect !== null) return;
    if ((step.onMissingAnchor ?? 'skip') === 'skip') goNext();
  }, [step, rect, goNext]);

  if (!tour || stepIndex === null || !step) return null;
  if (typeof document === 'undefined') return null;
  // rect === undefined means we're still hunting; don't paint a scrim yet.
  if (rect === undefined) return null;
  if (rect === null && (step.onMissingAnchor ?? 'skip') === 'skip') return null;

  return createPortal(
    <div className="dd-tour">
      <TourCutout rect={rect} onScrimClick={skip} />
      <TourStepCard
        step={step}
        index={stepIndex}
        total={tour.steps.length}
        rect={rect}
        onNext={goNext}
        onBack={goBack}
        onSkip={skip}
      />
    </div>,
    document.body,
  );
};

export default TourHost;
